import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";

import DosePlannerForm from "../../features/routine/components/DosePlannerForm";
import DoseActiveList from "../../features/routine/components/DoseActiveList";
import DoseHistoryList from "../../features/routine/components/DoseHistoryList";
import DoseEditModal from "../../features/routine/components/DoseEditModal";

import { generateCycleDates } from "../../features/routine/utils/generateCycleDates";
import useDosesData from "../../features/routine/hooks/useDosesData";

const STATUS = {
  PLANEJADA: "PLANEJADA",
  PROGRAMADA: "PROGRAMADA",
  PENDENTE: "PENDENTE",
  ATRASADA: "ATRASADA",
  APLICADA: "APLICADA",
  PULADA: "PULADA",
};

export default function Doses() {
  const {
    loading,
    message,
    setMessage,
    fetchData,
    selectableFlasks,
    getDoseFlask,
    concLabelForFlask,
    grouped,
    safeNumber,
    formatPtNumber,
    formatDateTimeBR,
    todayInputDate,
    nowInputTime,
    isValidHHMM,
    readLastDoseTime,
    writeLastDoseTime,
    splitISOToDateTimeInputs,
    combineDateTimeToISO,
    formatDelay,
    getId,
  } = useDosesData();

  const [doseMessages, setDoseMessages] = useState({});
  const [mode, setMode] = useState("SINGLE");
  const [showForm, setShowForm] = useState(false);

  const defaultTime = useMemo(() => readLastDoseTime(), [readLastDoseTime]);

  const [singleForm, setSingleForm] = useState({
    flaskId: "",
    dateLocal: todayInputDate(),
    timeLocal: defaultTime,
    dosageMg: "",
    notificationActive: true,
  });

  const [cycleForm, setCycleForm] = useState({
    flaskId: "",
    startDateLocal: todayInputDate(),
    startTimeLocal: defaultTime,
    dosageMg: "",
    frequencyType: "DAILY",
    everyNDays: 2,
    weeklyDay: 1,
    durationDays: 28,
    notificationActive: true,
  });

  const [showEdit, setShowEdit] = useState(false);
  const [editDose, setEditDose] = useState(null);
  const [editForm, setEditForm] = useState({
    flaskId: "",
    dateLocal: "",
    timeLocal: "",
    dosageMg: "",
    notificationActive: true,
  });

  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!showForm) return;

    const last = readLastDoseTime();

    setSingleForm((prev) => ({
      ...prev,
      timeLocal: isValidHHMM(prev.timeLocal) ? prev.timeLocal : last,
      dateLocal: prev.dateLocal || todayInputDate(),
    }));

    setCycleForm((prev) => ({
      ...prev,
      startTimeLocal: isValidHHMM(prev.startTimeLocal)
        ? prev.startTimeLocal
        : last,
      startDateLocal: prev.startDateLocal || todayInputDate(),
    }));
  }, [showForm, isValidHHMM, readLastDoseTime, todayInputDate]);

  const setDoseMessage = (doseId, text = "", type = "warning") => {
    const id = String(doseId ?? "");
    if (!id) return;

    setDoseMessages((prev) => ({
      ...prev,
      [id]: text ? { text, type } : undefined,
    }));
  };

  const clearDoseMessage = (doseId) => {
    const id = String(doseId ?? "");
    if (!id) return;

    setDoseMessages((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const isFlaskAvailable = (flask) => {
    if (!flask) return true;

    const status = String(flask?.status || "").toUpperCase();
    if (status === "UTILIZADO" || status === "ARQUIVADO") return false;

    return true;
  };

  const handleCreateSingle = async (e) => {
    e.preventDefault();
    setMessage({ text: "", type: "" });

    const flaskId = singleForm.flaskId;
    const dosage = safeNumber(singleForm.dosageMg);
    const dateISO = combineDateTimeToISO(
      singleForm.dateLocal,
      singleForm.timeLocal
    );

    if (!flaskId) {
      return setMessage({
        text: "Selecione o medicamento.",
        type: "error",
      });
    }

    if (!dateISO) {
      return setMessage({
        text: "Informe data e hora.",
        type: "error",
      });
    }

    if (!dosage || dosage <= 0) {
      return setMessage({
        text: "Informe uma dosagem válida (mg).",
        type: "error",
      });
    }

    try {
      await api.post("/doses", {
        date: dateISO,
        dosage,
        status: STATUS.PLANEJADA,
        flaskId,
        notification_active: !!singleForm.notificationActive,
      });

      writeLastDoseTime(singleForm.timeLocal);

      setSingleForm((prev) => ({
        ...prev,
        dosageMg: "",
      }));

      setMessage({
        text: "Dose planejada com sucesso.",
        type: "success",
      });

      setShowForm(false);
      await fetchData();
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setMessage({
        text: apiMessage || "Não foi possível planejar a dose.",
        type: "error",
      });
    }
  };

  const handleCreateCycle = async (e) => {
    e.preventDefault();
    setMessage({ text: "", type: "" });

    const flaskId = cycleForm.flaskId;
    const dosage = safeNumber(cycleForm.dosageMg);

    if (!flaskId) {
      return setMessage({
        text: "Selecione o medicamento.",
        type: "error",
      });
    }

    if (!dosage || dosage <= 0) {
      return setMessage({
        text: "Informe uma dosagem válida (mg).",
        type: "error",
      });
    }

    const dates = generateCycleDates({
      startDateLocal: cycleForm.startDateLocal,
      startTimeLocal: cycleForm.startTimeLocal,
      frequencyType: cycleForm.frequencyType,
      everyNDays: cycleForm.everyNDays,
      everyNHours: cycleForm.everyNHours,
      weeklyDay: cycleForm.weeklyDay,
      durationDays: cycleForm.durationDays,
    });

    if (dates.length === 0) {
      return setMessage({
        text: "Não foi possível gerar as datas do ciclo. Verifique as configurações.",
        type: "error",
      });
    }

    const payload = dates.map((date) => ({
      date,
      dosage,
      status: STATUS.PLANEJADA,
      flaskId,
      notification_active: !!cycleForm.notificationActive,
    }));

    try {
      await api.post("/doses/bulk", {
        doses: payload,
      });

      writeLastDoseTime(cycleForm.startTimeLocal);

      setMessage({
        text: `Ciclo criado: ${payload.length} doses planejadas.`,
        type: "success",
      });

      setShowForm(false);
      await fetchData();
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setMessage({
        text:
          apiMessage ||
          "Não foi possível criar o ciclo. Verifique a rota /doses/bulk no backend.",
        type: "error",
      });
    }
  };

  const applyDose = async (dose) => {
    setMessage({ text: "", type: "" });

    const id = getId(dose);
    const flask = getDoseFlask(dose);
    const medName = flask?.name || "este medicamento";

    clearDoseMessage(id);

    if (!isFlaskAvailable(flask)) {
      const text = `Sem estoque para aplicar ${medName}. Verifique os frascos.`;
      setDoseMessage(id, text, "warning");
      setMessage({ text, type: "warning" });
      return;
    }

    try {
      await api.put(`/doses/${id}`, {
        status: STATUS.APLICADA,
      });

      clearDoseMessage(id);

      setMessage({
        text: "Aplicação registrada com sucesso.",
        type: "success",
      });

      await fetchData();
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      const text = apiMessage || "Erro interno ao atualizar dose.";

      setDoseMessage(id, text, "warning");
      setMessage({ text, type: "warning" });

      await fetchData();
    }
  };

  const markSkipped = async (dose) => {
    const id = getId(dose);
    clearDoseMessage(id);

    try {
      await api.put(`/doses/${id}`, {
        status: STATUS.PULADA,
      });

      setMessage({
        text: "Dose marcada como pulada.",
        type: "success",
      });

      await fetchData();
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      const text = apiMessage || "Erro ao atualizar dose.";

      setDoseMessage(id, text, "error");
      setMessage({ text, type: "error" });
    }
  };

  const openEdit = (dose) => {
    setMessage({ text: "", type: "" });
    clearDoseMessage(getId(dose));

    const { date, time } = splitISOToDateTimeInputs(dose?.date);

    setEditDose(dose);
    setEditForm({
      flaskId: dose?.flaskId ? String(dose.flaskId) : "",
      dateLocal: date,
      timeLocal: time || readLastDoseTime(),
      dosageMg: String(dose?.dosage ?? ""),
      notificationActive: Boolean(dose?.notification_active ?? true),
    });
    setShowEdit(true);
  };

  const saveEdit = async () => {
    if (!editDose) return;

    const id = getId(editDose);
    const dosage = safeNumber(editForm.dosageMg);
    const dateISO = combineDateTimeToISO(editForm.dateLocal, editForm.timeLocal);
    const flaskId = editForm.flaskId;

    if (!flaskId) {
      return setMessage({
        text: "Selecione o medicamento.",
        type: "error",
      });
    }

    if (!dateISO) {
      return setMessage({
        text: "Informe data e hora.",
        type: "error",
      });
    }

    if (!dosage || dosage <= 0) {
      return setMessage({
        text: "Informe uma dosagem válida (mg).",
        type: "error",
      });
    }

    try {
      await api.put(`/doses/${id}`, {
        date: dateISO,
        dosage,
        flaskId,
        notification_active: !!editForm.notificationActive,
      });

      writeLastDoseTime(editForm.timeLocal);

      setShowEdit(false);
      setEditDose(null);
      clearDoseMessage(id);

      setMessage({
        text: "Dose atualizada com sucesso.",
        type: "success",
      });

      await fetchData();
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      const text = apiMessage || "Erro ao editar dose.";

      setDoseMessage(id, text, "error");
      setMessage({ text, type: "error" });
    }
  };

  const deleteDose = async (dose) => {
    const derived = dose?.__derivedStatus || dose?.status;
    const status = String(derived || "").toUpperCase();

    if (status === STATUS.APLICADA) return;

    const ok = window.confirm("Tem certeza que deseja excluir esta dose?");
    if (!ok) return;

    try {
      await api.delete(`/doses/${getId(dose)}`);
      clearDoseMessage(getId(dose));

      setMessage({
        text: "Dose excluída.",
        type: "success",
      });

      await fetchData();
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      setMessage({
        text: apiMessage || "Erro ao excluir dose.",
        type: "error",
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 py-2 animate-pulse">
        <div className="h-12 rounded-2xl skeleton-loading w-full" />
        <div className="h-32 rounded-3xl skeleton-loading w-full" />
        <div className="h-32 rounded-3xl skeleton-loading w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* BOTÃO DE AÇÃO PRINCIPAL LITI */}
      <button
        type="button"
        onClick={() => setShowForm((prev) => !prev)}
        className="btn-primary w-full h-12 text-xs font-bold uppercase tracking-wider shadow-md flex items-center justify-center gap-2"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d={showForm ? "M6 18L18 6M6 6l12 12" : "M12 4v16m8-8H4"} />
        </svg>
        <span>{showForm ? "Fechar Planejador" : "Planejar Novas Doses"}</span>
      </button>

      {/* MENSAGEM DE FEEDBACK */}
      {message.text && (
        <div
          className={`rounded-2xl border p-3.5 text-center text-xs font-semibold shadow-xs animate-in fade-in duration-200 ${
            message.type === "success"
              ? "border-[#CBF1DB] bg-[#CBF1DB]/60 text-emerald-900"
              : message.type === "warning"
              ? "border-amber-200 bg-amber-50 text-amber-900"
              : "border-red-200 bg-red-50 text-red-800"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* FORMULÁRIO DE PLANEJAMENTO */}
      {showForm && (
        <div className="liti-card p-5 border border-[#ECE7FA] shadow-lg animate-in fade-in duration-200">
          <DosePlannerForm
            mode={mode}
            setMode={setMode}
            selectableFlasks={selectableFlasks}
            concLabelForFlask={concLabelForFlask}
            singleForm={singleForm}
            setSingleForm={setSingleForm}
            cycleForm={cycleForm}
            setCycleForm={setCycleForm}
            onSubmitSingle={handleCreateSingle}
            onSubmitCycle={handleCreateCycle}
            isValidHHMM={isValidHHMM}
            writeLastDoseTime={writeLastDoseTime}
            todayInputDate={todayInputDate}
            nowInputTime={nowInputTime}
          />
        </div>
      )}

      {/* SEÇÃO DE DOSES ATIVAS LITI */}
      <section className="space-y-3 pt-1">
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#6C54D8]" />
            <h3 className="text-sm font-bold tracking-tight text-[#24152F] font-serif">
              Doses Ativas
            </h3>
          </div>

          <span className="rounded-full bg-[#ECE7FA] px-3 py-0.5 text-[11px] font-bold text-[#6C54D8]">
            {grouped.active.length} registro(s)
          </span>
        </div>

        <DoseActiveList
          doses={grouped.active}
          getId={getId}
          getDoseFlask={getDoseFlask}
          formatDateTimeBR={formatDateTimeBR}
          formatPtNumber={formatPtNumber}
          formatDelay={formatDelay}
          applyDose={applyDose}
          markSkipped={markSkipped}
          openEdit={openEdit}
          deleteDose={deleteDose}
          doseMessages={doseMessages}
        />
      </section>

      {/* HISTÓRICO DE DOSES */}
      <div className="pt-2">
        <DoseHistoryList
          doses={grouped.history}
          showHistory={showHistory}
          setShowHistory={setShowHistory}
          getId={getId}
          getDoseFlask={getDoseFlask}
          formatDateTimeBR={formatDateTimeBR}
          formatPtNumber={formatPtNumber}
        />
      </div>

      {/* MODAL DE EDIÇÃO */}
      <DoseEditModal
        open={showEdit}
        onClose={() => setShowEdit(false)}
        editForm={editForm}
        setEditForm={setEditForm}
        selectableFlasks={selectableFlasks}
        getId={getId}
        concLabelForFlask={concLabelForFlask}
        isValidHHMM={isValidHHMM}
        writeLastDoseTime={writeLastDoseTime}
        onSave={saveEdit}
      />
    </div>
  );
}